import React, { useEffect, useState } from "react";
import { Megaphone, Check, Crown, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { Overline } from "@/components/Cards";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const inputCls = "w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#00E5FF]";
const PAGES = ["home", "directory", "events", "offers", "promotions", "tours", "guides"];
const CATS = ["Places to Eat + Drink", "Galleries + Studios", "Specialty Shops + Salons", "Community + Business Services"];
const GROUPS = ["Display", "Email", "Members"];
const unitLabel = { day: "/ day", send: "/ send", blast: "flat", flat: "flat" };

export default function Advertise() {
  const { user } = useAuth();
  const [options, setOptions] = useState([]);
  const [sel, setSel] = useState(null);
  const [f, setF] = useState({ quantity: 1, start_date: "", end_date: "", advertiser_name: "", advertiser_email: "", headline: "", target_url: "", image: "", page: "home", category: CATS[0], body: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/ads/options").then((r) => setOptions(r.data)); }, []);

  const dayCount = (() => {
    if (f.start_date && f.end_date) {
      const d = Math.round((new Date(f.end_date) - new Date(f.start_date)) / 86400000) + 1;
      return d > 0 ? d : 1;
    }
    return Math.max(1, Number(f.quantity) || 1);
  })();
  const qtyForPrice = sel ? (sel.unit === "day" ? dayCount : (sel.unit === "send" ? Math.max(1, Number(f.quantity) || 1) : 1)) : 1;
  const price = sel ? sel.price * qtyForPrice : 0;

  const checkout = async () => {
    if (!sel) return;
    if (sel.member_only && !user) { toast.error("This placement is for members. Sign in to your business account first."); return; }
    if (!f.advertiser_name || !f.advertiser_email) { toast.error("Please add your name and email."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/ads/checkout", {
        option_key: sel.key, quantity: Number(f.quantity) || 1, origin_url: window.location.origin,
        advertiser_name: f.advertiser_name, advertiser_email: f.advertiser_email,
        headline: f.headline, body: f.body, target_url: f.target_url, image: f.image,
        page: sel.needs.includes("page") ? f.page : "", category: sel.needs.includes("category") ? f.category : "",
        start_date: sel.unit === "day" ? f.start_date : "", end_date: sel.unit === "day" ? f.end_date : "",
      });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div>
      <Seo title="Advertise With Us" description="Advertise on Visit RiNo — homepage banners, category sponsorships, newsletter ads, sponsored email blasts, featured events, profiles, articles, editorial features and more. From $10/day." />
      <PageHeader overline="Reach the District" title="Advertise With Visit RiNo" subtitle="Put your brand in front of everyone exploring Denver's RiNo Art District. Flexible placements from $10/day — members save 20%." image="https://static.prod-images.emergentagent.com/jobs/aa1495be-2be9-4669-b2d1-d5e4a9831348/images/f5c799719e93a395fa54ed1753530d50156c290a8635e4b414ec1dde8233e4a5.jpeg" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Options */}
        <div className="lg:col-span-7">
          {GROUPS.map((g) => (
            <div key={g} className="mb-8">
              <Overline>{g === "Members" ? "Members Only" : g}</Overline>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                {options.filter((o) => o.group === g).map((o) => {
                  const active = sel?.key === o.key;
                  return (
                    <button key={o.key} onClick={() => { setSel(o); setF((p) => ({ ...p, quantity: 1 })); }} data-testid={`ad-option-${o.key}`}
                      className={`text-left border p-5 transition-colors ${active ? "border-[#FF0055] bg-[#FF0055]/10" : "border-white/10 bg-[#141414] hover:border-white/30"}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-display font-bold text-white">{o.label}</span>
                        {o.member_only && <Crown size={14} className="text-[#FFEA00]" />}
                      </div>
                      <p className="text-neutral-400 text-sm mt-1.5 leading-relaxed">{o.desc}</p>
                      <div className="mt-3 font-display font-extrabold text-2xl text-[#00E5FF]">${o.price}<span className="text-neutral-500 text-sm font-normal"> {unitLabel[o.unit]}</span></div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Config panel */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-24 border border-white/10 bg-[#141414] p-6">
            <div className="flex items-center gap-2 text-[#FF0055] font-accent text-sm mb-4"><Megaphone size={16} /> Build Your Ad</div>
            {!sel ? (
              <p className="text-neutral-500 text-sm">Select a placement to get started.</p>
            ) : (
              <div className="space-y-4">
                <div className="border border-white/10 p-3">
                  <div className="text-white font-semibold">{sel.label}</div>
                  {sel.member_only && !user && <div className="text-[#FFEA00] text-xs mt-1">Members only — sign in to your business account to book this.</div>}
                </div>
                {sel.unit === "day" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-neutral-400 text-xs">Start date</label>
                      <input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="ad-start-date" className={`mt-1 ${inputCls}`} />
                    </div>
                    <div>
                      <label className="text-neutral-400 text-xs">End date</label>
                      <input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="ad-end-date" className={`mt-1 ${inputCls}`} />
                    </div>
                    <div className="col-span-2 text-neutral-500 text-xs">{dayCount} day{dayCount > 1 ? "s" : ""} — spots auto-expire at the end date.</div>
                  </div>
                )}
                {sel.unit === "send" && (
                  <div>
                    <label className="text-neutral-400 text-xs">Number of sends</label>
                    <input type="number" min="1" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="ad-quantity" className={`mt-1 ${inputCls}`} />
                  </div>
                )}
                {sel.needs.includes("page") && (
                  <div>
                    <label className="text-neutral-400 text-xs">Page to sponsor</label>
                    <select value={f.page} onChange={(e) => setF({ ...f, page: e.target.value })} data-testid="ad-page" className={`mt-1 ${inputCls}`}>
                      {PAGES.map((p) => <option key={p} value={p} className="bg-[#141414]">{p}</option>)}
                    </select>
                  </div>
                )}
                {sel.needs.includes("category") && (
                  <div>
                    <label className="text-neutral-400 text-xs">Category page</label>
                    <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="ad-category" className={`mt-1 ${inputCls}`}>
                      {CATS.map((c) => <option key={c} value={c} className="bg-[#141414]">{c}</option>)}
                    </select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <input placeholder="Your name" value={f.advertiser_name} onChange={(e) => setF({ ...f, advertiser_name: e.target.value })} data-testid="ad-name" className={inputCls} />
                  <input placeholder="Email" type="email" value={f.advertiser_email} onChange={(e) => setF({ ...f, advertiser_email: e.target.value })} data-testid="ad-email" className={inputCls} />
                </div>
                <input placeholder="Headline / ad copy" value={f.headline} onChange={(e) => setF({ ...f, headline: e.target.value })} data-testid="ad-headline" className={inputCls} />
                <input placeholder="Link URL (where the ad clicks to)" value={f.target_url} onChange={(e) => setF({ ...f, target_url: e.target.value })} data-testid="ad-target" className={inputCls} />
                <input placeholder="Image URL (optional)" value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} data-testid="ad-image" className={inputCls} />

                <div className="border-t border-white/10 pt-4">
                  <div className="flex justify-between text-neutral-300 text-sm"><span>Subtotal</span><span data-testid="ad-subtotal">${price.toFixed(2)}</span></div>
                  {user?.membership_tier === "premium" ? (
                    <>
                      <div className="flex justify-between text-[#00FF66] text-sm mt-1"><span>Premium member discount (20%)</span><span data-testid="ad-discount">-${(price * 0.2).toFixed(2)}</span></div>
                      <div className="flex justify-between text-white text-sm font-semibold mt-1"><span>Total</span><span data-testid="ad-total">${(price * 0.8).toFixed(2)}</span></div>
                    </>
                  ) : (
                    <div className="flex justify-between text-[#00FF66] text-xs mt-1"><span>Premium members save 20% at checkout</span></div>
                  )}
                  <Button onClick={checkout} disabled={busy} data-testid="ad-checkout-btn" className="w-full mt-4 bg-[#FF0055] hover:bg-[#ff2d74] rounded-none h-11 font-semibold">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Continue to Payment
                  </Button>
                  <p className="text-neutral-600 text-xs mt-2 text-center">Test card 4242 4242 4242 4242</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
